import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import { etiquetaDeTipo } from './inventarioMovimientosTipos.js';

/**
 * VEC-3313 Scenario 05 — "muestra los movimientos registrados para ese ítem en el depósito
 * seleccionado".
 *
 * Modal ACOTADO a un ítem (y, si hay filtro, a un depósito). La pantalla completa de historial, con
 * sus propios filtros y su detalle por movimiento, es VEC-3315: el reparto entre las dos está
 * acordado para no construir lo mismo dos veces (decisión C del análisis).
 *
 * EL SIGNO ES DE LA RELACIÓN MOVIMIENTO-UBICACIÓN, NO DEL MOVIMIENTO. En una transferencia el mismo
 * movimiento es egreso para el origen e ingreso para el destino, así que:
 *
 *  - Con depósito filtrado, el backend manda `esIngreso` true/false y la cantidad se muestra con
 *    + o −, que es lo que pide la card.
 *  - Sin depósito filtrado (el consolidado), `esIngreso` viene en NULL a propósito: la fila mezcla
 *    los dos lados de una transferencia y el signo sería ambiguo. Se muestra SIN signo. No se asume
 *    un default: la dirección se lee de Origen → Destino.
 *
 * La regla vive en el backend (ItemStockRepository::esIngreso) y acá solo se representa.
 */

const BASE_URL = '/inventario/stock';

const COLUMN_COUNT = 7;

const PAGINATION_INIT = { page: 0, perPage: 10 };

const InventarioStockMovimientosModal = (props) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });

  const item = props.item;

  // La ubicación llega DESARMADA en primitivos y no como objeto: el efecto que trae los datos la
  // tiene como dependencia, y un objeto nuevo en cada render de la grilla la haría cambiar de
  // identidad siempre — un fetch por render, en bucle.
  const ubicacionType = props.ubicacionType;
  const ubicacionId = props.ubicacionId;
  const ubicacionNombre = props.ubicacionNombre;

  // Al cambiar de ítem se vuelve a la primera página: si no, abrir un ítem con 3 movimientos
  // estando en la página 4 del anterior muestra el modal vacío. Se compara antes de setear para no
  // disparar un segundo fetch cuando ya se está en la primera página, que es el caso habitual.
  useEffect(() => {
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [item]);

  useEffect(() => {
    if (!item) {
      setRows([]);
      setCount(0);
      return;
    }

    setLoading(true);
    Utils.getData(`${BASE_URL}/${item.id}/movimientos${getQuery()}`)
      .then((data) => {
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
      })
      .catch(() => {
        setRows([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, [item, ubicacionType, ubicacionId, pagination]);

  /**
   * La ubicación viaja como PAR (tipo + id) y solo cuando hay depósito elegido: el backend rechaza
   * el par a medias con 400, así que mandar el tipo con el id vacío rompería el consolidado.
   */
  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;
    if (ubicacionId) {
      query += '&ubicacionType=' + encodeURIComponent(ubicacionType);
      query += '&ubicacionId=' + encodeURIComponent(ubicacionId);
    }
    return query;
  };

  const formatEntero = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 0);
  };

  /**
   * Sin símbolo de moneda: los valores monetarios de esta pantalla están declarados fuera de alcance
   * hasta VEC-3325 (multimoneda). El número se muestra igual —el permiso
   * INVENTARIO_STOCK_VISUALIZAR_MOVIMIENTOS existe justamente porque el historial expone costos—,
   * pero sin comprometerse con una moneda que el modelo todavía no tiene.
   */
  const formatCosto = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 2);
  };

  /** `esIngreso` en null = consolidado sin filtro de depósito: la cantidad va sin signo. */
  const renderCantidad = (row) => {
    const cantidad = formatEntero(row.cantidad);

    if (row.esIngreso === true) {
      return <span className="text-success" style={{ fontWeight: 600 }}>{'+' + cantidad}</span>;
    }
    if (row.esIngreso === false) {
      return <span className="text-danger" style={{ fontWeight: 600 }}>{'−' + cantidad}</span>;
    }
    return cantidad;
  };

  const nombreUbicacion = (u) => (u && u.nombre ? u.nombre : '-');

  const tituloDeposito = ubicacionId ? ubicacionNombre : null;

  // 1140px es el ancho de `.modal-xl` de Bootstrap, el mismo que usa el historial de Mano de Obra.
  // Va inline y no como clase porque Material-UI inyecta sus propias reglas de `max-width` en el
  // head y le ganaría en especificidad a `.modal-xl`.
  return (
    <Dialog open={!!item} onClose={props.onClose} maxWidth={false} fullWidth PaperProps={{ style: { maxWidth: 1140 } }}>
      <DialogTitle disableTypography className="modal-header bg-fleet">
        <h4 className="modal-title text-white mb-0">
          <i className="la la-history align-middle icon-modal-title"></i>
          <FormattedMessage id="inventarioStock.movimientos.titulo" defaultMessage=" Movimientos del ítem: " />
          {item ? item.descripcion : ''}
        </h4>
      </DialogTitle>

      <DialogContent dividers className="modal-grey">
        {/* Qué recorte se está viendo. Sin esto, el consolidado y el de un depósito son dos tablas
            idénticas con números distintos y nada que las distinga. */}
        <div className="text-muted mb-2" style={{ fontSize: 12 }}>
          {tituloDeposito ? (
            <FormattedMessage
              id="inventarioStock.movimientos.alcance_deposito"
              defaultMessage="Movimientos en el depósito {deposito}. El signo indica si el ítem entró o salió de ese depósito."
              values={{ deposito: <b>{tituloDeposito}</b> }}
            />
          ) : (
            <FormattedMessage
              id="inventarioStock.movimientos.alcance_consolidado"
              defaultMessage="Movimientos en todos los depósitos. La dirección se lee en las columnas Origen y Destino."
            />
          )}
        </div>

        <TableContainer>
          <Table aria-label="inventario stock movimientos table" className="table-hover">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">
                  <FormattedMessage id="inventarioStock.movimientos.column_fecha" defaultMessage="Fecha" />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <FormattedMessage id="inventarioStock.movimientos.column_tipo" defaultMessage="Tipo" />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <FormattedMessage id="inventarioStock.movimientos.column_origen" defaultMessage="Origen" />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <FormattedMessage id="inventarioStock.movimientos.column_destino" defaultMessage="Destino" />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <FormattedMessage id="inventarioStock.movimientos.column_cantidad" defaultMessage="Cantidad" />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <FormattedMessage id="inventarioStock.movimientos.column_costo" defaultMessage="Costo unitario" />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <FormattedMessage id="inventarioStock.movimientos.column_usuario" defaultMessage="Responsable" />
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <GridLoadingRow visible={loading} columns={COLUMN_COUNT} />
              {!loading &&
                (rows.length > 0 ? (
                  rows.map((row) => (
                    <TableRow key={'MovRow_' + row.id}>
                      <StyledTableCell align="left">
                        {row.fecha ? LocaleFormat.formatDate(row.fecha) : '-'}
                      </StyledTableCell>
                      <StyledTableCell align="left">{etiquetaDeTipo(intl, row.tipo)}</StyledTableCell>
                      <StyledTableCell align="left">{nombreUbicacion(row.ubicacionOrigen)}</StyledTableCell>
                      <StyledTableCell align="left">{nombreUbicacion(row.ubicacionDestino)}</StyledTableCell>
                      <StyledTableCell align="right">{renderCantidad(row)}</StyledTableCell>
                      <StyledTableCell align="right">{formatCosto(row.costo)}</StyledTableCell>
                      <StyledTableCell align="left">{row.usuario || '-'}</StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <GridEmptyRow
                    columns={COLUMN_COUNT}
                    messageId="inventarioStock.movimientos.no_data_table"
                    label="Este ítem no tiene movimientos registrados."
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <GridPagination
          count={count}
          page={pagination.page}
          perPage={pagination.perPage}
          onPageChange={(event, newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
          onPerPageChange={(event) =>
            setPagination({ page: 0, perPage: parseInt(event.target.value, 10) })
          }
        />
      </DialogContent>

      <DialogActions className="modal-grey">
        <button type="button" className="btn btn-default btn-fleet" onClick={props.onClose}>
          <FormattedMessage id="Ok" defaultMessage="Ok" />
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InventarioStockMovimientosModal;
